import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import faqData from '../data/faqData.json';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown, { Components } from 'react-markdown';

interface FAQItem {
  id: string;
  question: string;
  answer?: string;
  filePath?: string;
  linkURL?: string;
  hidden?: boolean;
}

interface RouteParams {
  [key: string]: string | undefined;
  faqId: string;
}

const FAQPage: React.FC = () => {
  const { faqId } = useParams<RouteParams>();
  const navigate = useNavigate();
  const faqItem = (faqData as FAQItem[]).find(item => item.id === faqId);
  const [markdownContent, setMarkdownContent] = useState<string>('');

  const customMarkdownComponents: Components = {
    img: ({ node, ...props }) => (
      <img
        {...props}
        style={{ maxWidth: '100%', height: 'auto', margin: '1em 0', borderRadius: '4px', display: 'block' }}
        alt={props.alt || ''}
      />
    ),
  };

  useEffect(() => {
    if (!faqItem || faqItem.hidden) {
      navigate('/404', { replace: true });
      return;
    }

    if (faqItem.linkURL) {
      navigate(faqItem.linkURL, { replace: true });
      return;
    }

    if (faqItem.filePath) {
      fetch(process.env.PUBLIC_URL + faqItem.filePath)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(text => setMarkdownContent(text))
        .catch(error => {
          console.error('Failed to fetch FAQ content:', error);
          setMarkdownContent('Failed to load content.');
        });
    } else if (faqItem.answer) {
      setMarkdownContent(faqItem.answer);
    }
  }, [faqItem, navigate, faqId]);

  if (!faqItem || faqItem.hidden) {
    return null;
  }

  return (
    <div className="faq-page">
      <Helmet>
        <title>{faqItem.question} | Fat Scientist FAQ</title>
        <meta name="description" content={faqItem.question} />
      </Helmet>
      <h2>{faqItem.question}</h2>
      <ReactMarkdown components={customMarkdownComponents}>{markdownContent}</ReactMarkdown>
      <nav>
        <h4>More Questions:</h4>
        <ul>
          {(faqData as FAQItem[])
            .filter(item => !item.hidden)
            .map(item => (
              <li key={item.id}>
                <a href={item.linkURL ? item.linkURL : `/faq/${item.id}`}>{item.question}</a>
              </li>
            ))}
        </ul>
      </nav>
    </div>
  );
};

export default FAQPage;
